/**
 * Lo que tiene que pasar al cambiar de pantalla, y no pasaba.
 *
 * En una SPA el navegador no hace nada al navegar: no sube la pagina y no
 * mueve el foco. Se notaba en las dos puntas. Quien venia mirando los
 * servicios de un negocio y apretaba "Reservar" empezaba la reserva a mitad de
 * pantalla, sin ver el primer paso. Y quien navega con teclado o con lector
 * seguia parado en el enlace que apreto, en una pagina que ya no existe.
 *
 * El foco va al contenedor de la ruta y no al `h1`: las pantallas se cargan
 * diferidas, asi que cuando esto corre el titulo todavia puede no existir. El
 * contenedor esta siempre.
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

/** Ancla del salto al contenido y destino del foco al navegar. */
export const ID_CONTENIDO = "contenido";

export function FocoDeRuta(): null {
  const { pathname } = useLocation();
  const primera = useRef(true);

  useEffect(() => {
    // En la primera carga manda el navegador: puede venir con un ancla, o
    // restaurando la posicion de una recarga.
    if (primera.current) {
      primera.current = false;
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    const destino = document.getElementById(ID_CONTENIDO);
    if (destino) destino.focus({ preventScroll: true });
  }, [pathname]);

  return null;
}

/**
 * "Saltar al contenido".
 *
 * Invisible hasta que se lo enfoca con el teclado. Sin esto, entrar a
 * cualquier pantalla del panel obliga a pasar por las ocho secciones del menu
 * antes de llegar a lo que se vino a hacer, en cada pagina.
 */
export function SaltarAlContenido(): ReactNode {
  return (
    <a
      href={`#${ID_CONTENIDO}`}
      className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
    >
      Saltar al contenido
    </a>
  );
}
